import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../styles/QuizPage.css";

const OPTION_LABELS = ["A", "B", "C", "D", "E", "F"];

export default function QuizPage(props) {
  const { subject,quiz_file_name, id: paramId } = useParams();
  const id = props?.id ?? paramId;
  const navigate = useNavigate();

  const [module, setModule] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [progress, setProgress] = useState(0);

  const [totalTime, setTotalTime] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);

  const quizJsonPath =
    props?.quizPath ??
    (subject ? `/practice_mcqs/${subject}/${quiz_file_name}.json` : "/questions.json");

  useEffect(() => {
    if (!id) return;
    fetch(quizJsonPath)
      .then((r) => r.json())
      .then((data) => {
        let mod = null;
        const mid = parseInt(id);
        if (Array.isArray(data.modules)) {
          mod = data.modules.find((m) => m.id === mid) || data.modules[0];
        } else if (Array.isArray(data)) {
          mod = { id: mid, title: "Quiz", duration: data.duration, questions: data };
        } else if (data.questions) {
          mod = { id: mid, title: data.title || "Quiz", duration: data.duration, questions: data.questions };
        }
        setModule(mod);
        setTotalTime(mod.duration || 600);
        setTimeLeft(mod.duration || 600);
      })
      .catch((err) => { console.error("Error loading quiz:", err); setModule(null); });
  }, [quizJsonPath, id]);

  useEffect(() => {
    if (!module) return;
    setCurrentQ(0); setSelected(null); setFeedback(null);
    setShowExplanation(false); setScore(0);
  }, [module]);

  useEffect(() => {
    if (module) setProgress(((currentQ + 1) / module.questions.length) * 100);
  }, [currentQ, module]);

  useEffect(() => {
    if (!module || timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { clearInterval(timer); finishQuiz(); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [module, timeLeft]);

  if (!module) {
    return (
      <div className="qp-loading">
        <div className="qp-spinner" />
        <span>Loading quiz…</span>
      </div>
    );
  }

  const q = module.questions[currentQ];
  const isLastQuestion = currentQ + 1 === module.questions.length;

  function submitAnswer(opt) {
    if (feedback) return;
    setSelected(opt);
    if (opt === q.answer) { setScore((s) => s + 1); setFeedback("correct"); }
    else { setFeedback("wrong"); }
    setShowExplanation(true);
  }

  function next() {
    setFeedback(null); setSelected(null); setShowExplanation(false);
    if (currentQ + 1 < module.questions.length) setCurrentQ((c) => c + 1);
    else finishQuiz();
  }

  function finishQuiz() {
    const resultState = {
      moduleTitle: module.title, total: module.questions.length,
      score, moduleId: parseInt(id), subject, timeTaken: totalTime - timeLeft,
    };
    if (props?.embedded && typeof props.onFinish === "function") props.onFinish(resultState);
    else navigate("/result", { state: resultState });
  }

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const timerPct = totalTime > 0 ? (timeLeft / totalTime) * 100 : 100;
  const isDanger = timeLeft <= 30;

  return (
    <div className="qp-page">
      {/* Top bar */}
      <header className="qp-topbar">
        <div className="qp-topbar-inner">
          <div className="qp-module-label">
            <span className="qp-module-dot" />
            {module.title}
          </div>

          <div className="qp-meta">
            <span className="qp-score-pill">
              {score} / {module.questions.length}
            </span>
            <div className={`qp-timer ${isDanger ? "danger" : ""}`}>
              <svg className="qp-timer-ring" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="currentColor" strokeOpacity="0.15" strokeWidth="3" />
                <circle
                  cx="18" cy="18" r="15.9" fill="none"
                  stroke="currentColor" strokeWidth="3"
                  strokeDasharray={`${timerPct} 100`}
                  strokeLinecap="round"
                  transform="rotate(-90 18 18)"
                />
              </svg>
              <span className="qp-timer-text">
                {minutes}:{seconds.toString().padStart(2, "0")}
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="qp-main">
        <div className="qp-shell">

          {/* Progress strip */}
          <div className="qp-progress-row">
            <div className="qp-progress-track">
              <div className="qp-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="qp-progress-label">
              {currentQ + 1} <span className="qp-progress-of">of</span> {module.questions.length}
            </span>
          </div>

          {/* Question card */}
          <div className="qp-card">
            <div className="qp-q-meta">
              <span className="qp-q-badge">Q{currentQ + 1}</span>
            </div>
            <p className="qp-question">{q.question}</p>

            <div className="qp-options">
              {q.options.map((opt, idx) => {
                let state = "";
                if (feedback) {
                  if (opt === q.answer) state = "correct";
                  else if (opt === selected) state = "wrong";
                  else state = "dimmed";
                }
                return (
                  <button
                    key={idx}
                    onClick={() => submitAnswer(opt)}
                    disabled={!!feedback}
                    className={`qp-option ${state}`}
                    aria-pressed={selected === opt}
                  >
                    <span className="qp-option-letter">{OPTION_LABELS[idx]}</span>
                    <span className="qp-option-text">{opt}</span>
                    {feedback && opt === q.answer && (
                      <span className="qp-option-icon">✓</span>
                    )}
                    {feedback && opt === selected && opt !== q.answer && (
                      <span className="qp-option-icon">✗</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Feedback + explanation */}
            {feedback && (
              <div className={`qp-result ${feedback}`}>
                <div className="qp-result-header">
                  {feedback === "correct" ? (
                    <><span className="qp-result-icon">✓</span> Correct</>
                  ) : (
                    <><span className="qp-result-icon">✗</span> Incorrect — correct answer: <strong>{q.answer}</strong></>
                  )}
                </div>
                {q.explanation && (
                  <p className="qp-result-explanation">{q.explanation}</p>
                )}
              </div>
            )}
          </div>

          {/* Action row */}
          {feedback && (
            <div className="qp-action-row">
              <button onClick={next} className={`qp-next-btn ${isLastQuestion ? "finish" : ""}`}>
                {isLastQuestion ? "Finish Quiz" : "Next Question"}
                <span className="qp-next-arrow">{isLastQuestion ? "✓" : "→"}</span>
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
